import React, { useMemo, useRef, useState } from 'react';
import LabelPrintModal from '../LabelPrintModal';
import ProductionTiffExportModal from '../ProductionTiffExportModal';
import Modal from '../Modal';
import PrintTogether from './PrintTogether';
import '../../pages/BatchPrint.css';

// Production paperwork on the Order Desk batch screen: production sheet, fabric labels and print files (TIFF).
// Same sheet layout, label rows and TIFF window as the Production page, so both give the same paper. No CSV (owner,
// 13 Oct: the old way, not needed). Loads the full batch only when a button is used.
const n2 = (v) => (Number(v) || 0).toFixed(2);
const orderNo = (num, source) => {
  const raw = String(num || '').trim();
  if (!raw || String(source || '').trim() !== 'Shopify') return raw;
  return raw.endsWith('00') ? raw : `${raw}00`;
};
const fmtDate = (v) => {
  const ms = v?._seconds ? v._seconds * 1000 : v?.seconds ? v.seconds * 1000 : Date.parse(v || '');
  return Number.isFinite(ms) ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '—';
};

// The printed production sheet: header, fabric summary, then every SKU with its orders
export function BatchPrintSheet({ batch }) {
  const items = batch.consolidatedItems || [];
  const summary = Object.entries(items.reduce((s, i) => { const f = i.fabricName || 'Unspecified Fabric'; s[f] = (s[f] || 0) + (Number(i.totalQuantity) || 0); return s; }, {}))
    .map(([fabricName, totalQuantity]) => ({ fabricName, totalQuantity })).sort((a, b) => a.fabricName.localeCompare(b.fabricName));
  const total = summary.reduce((t, x) => t + x.totalQuantity, 0);
  const mixed = String(batch.sourceType || '').toLowerCase() === 'po_bulk' || String(batch.batchQuality || '').toLowerCase() === 'mixed';
  const row = (item, key) => {
    const off = item.status === 'unavailable';
    const rec = Number(item.receivedQuantity) || 0;
    const alloc = (item.sourceOrders || []).reduce((s, so) => s + (Number(so.allocatedQuantity) || 0), 0);
    return (
      <tr key={key} style={off ? { opacity: 0.6 } : {}}>
        <td className="print-image-cell">{item.imageUrl ? <img src={item.imageUrl} alt={item.sku} className="print-item-image" loading="lazy" /> : <div className="print-no-image">No Image</div>}</td>
        <td className="sku-cell"><span style={off ? { textDecoration: 'line-through' } : {}}>{item.sku}</span></td>
        <td>{item.fabricName}</td>
        <td className="num-col">{n2(item.totalQuantity)}</td>
        <td className="num-col">{n2(rec)}</td>
        <td className="num-col">{n2(alloc)}</td>
        <td className="num-col">{n2(Math.max(0, rec - alloc))}</td>
        <td className="num-col">{n2(off ? 0 : Math.max(0, (Number(item.totalQuantity) || 0) - rec))}</td>
        <td><ul className="source-orders-list">{(item.sourceOrders || []).map((o, i) => <li key={o.lineItemId || i}><span>#{orderNo(o.orderNumber, o.source)}</span><span>{n2(o.quantity)}m</span></li>)}</ul></td>
      </tr>
    );
  };
  const byQuality = items.reduce((acc, i) => { const q = String(i.batchQuality || 'Unknown').trim() || 'Unknown'; (acc[q] = acc[q] || []).push(i); return acc; }, {});
  return (
    <div className="batch-print-container">
      <header className="print-header"><h1>{batch.batchId}</h1></header>
      <div className="batch-details-grid">
        <div className="detail-item"><span className="detail-label">Batch ID</span><span className="detail-value">{batch.batchId}</span></div>
        <div className="detail-item"><span className="detail-label">Quality</span><span className="detail-value">{batch.batchQuality}</span></div>
        <div className="detail-item"><span className="detail-label">Created</span><span className="detail-value">{fmtDate(batch.createdAt)}</span></div>
      </div>
      {summary.length > 0 && (
        <div className="print-summary-section">
          <h2>Fabric Summary</h2>
          <table className="print-items-table summary-table">
            <thead><tr><th>Fabric Name</th><th>Total Quantity (m)</th></tr></thead>
            <tbody>{summary.map((x) => <tr key={x.fabricName}><td>{x.fabricName}</td><td className="num-col">{n2(x.totalQuantity)}</td></tr>)}</tbody>
            <tfoot><tr><td><strong>Total</strong></td><td className="num-col"><strong>{n2(total)}</strong></td></tr></tfoot>
          </table>
        </div>
      )}
      <div className="print-items-section">
        <h2>SKU Details</h2>
        <table className="print-items-table">
          <thead><tr><th>Image</th><th>SKU</th><th>Fabric</th><th className="num-col">Total Qty (m)</th><th className="num-col">Received Total (m)</th><th className="num-col">Allocated to Orders (m)</th><th className="num-col">To Stock (m)</th><th className="num-col">Pending Qty (m)</th><th>Source Orders</th></tr></thead>
          <tbody>
            {mixed
              ? Object.keys(byQuality).sort().flatMap((q) => [<tr key={`h-${q}`} className="print-quality-heading-row"><td colSpan={9}>{q}</td></tr>, ...byQuality[q].map((i) => row(i, `${q}_${i.sku}`))])
              : items.map((i) => row(i, i.sku))}
          </tbody>
        </table>
      </div>
      <footer className="print-footer" />
    </div>
  );
}

// Print a rendered sheet through a hidden frame (same as the Production page): styles inlined, images loaded first
function printNode(node, title) {
  if (!node) return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const css = Array.from(document.styleSheets).map((sh) => { try { return Array.from(sh.cssRules).map((r) => r.cssText).join('\n'); } catch (e) { return ''; } }).join('\n');
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><base href="${window.location.origin}/"><style>${css}</style><style>.batch-print-container{display:block !important;} body{background:#fff;margin:0;}</style></head><body>${node.outerHTML.replace(/loading="lazy"/g, 'loading="eager"')}</body></html>`);
  doc.close();
  // "Save as PDF" names the file after the page title: use the batch number while printing, then put it back
  const pageTitle = document.title;
  const go = () => {
    const w = iframe.contentWindow;
    document.title = title;
    w.addEventListener('afterprint', () => setTimeout(() => { document.title = pageTitle; iframe.remove(); }, 500));
    w.focus(); w.print();
    setTimeout(() => { document.title = pageTitle; }, 60000); // safety if afterprint never fires
  };
  const pending = Array.from(doc.images).filter((i) => !i.complete);
  if (!pending.length) { setTimeout(go, 300); return; }
  let left = pending.length; let done = false;
  const once = () => { if (!done) { done = true; go(); } };
  pending.forEach((img) => { const settle = () => { left -= 1; if (left <= 0) once(); }; img.addEventListener('load', settle); img.addEventListener('error', settle); });
  setTimeout(once, 8000);
}

// One label per order line of the batch (same rows as the Production page)
export function batchLabelRows(batch) {
  const rows = [];
  (batch?.consolidatedItems || []).forEach((item) => {
    if (item?.status === 'unavailable') return;
    const sku = String(item?.sku || '').trim().toUpperCase();
    if (!sku) return;
    (item.sourceOrders || []).forEach((o, i) => {
      const qty = Number(o?.quantity || o?.allocatedQuantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) return;
      const no = orderNo(o.orderNumber || o.orderId, o.source);
      rows.push({ batchId: batch.batchId, batchNumber: batch.batchId, sku, qty, orderNumber: no, orderDisplayId: no, lineItemId: o.lineItemId || `${sku}_${i}`,
        fabricName: item.fabricName || '', productTitle: item.fabricName || '', imageUrl: item.imageUrl || item.productImageUrl || '', productImageUrl: item.imageUrl || item.productImageUrl || '' });
    });
  });
  return rows;
}

// Folder for a print run: the batch itself, or e.g. "PURE-480+483+485" when printed together
export function runFolderName(batchIds) {
  if (batchIds.length <= 1) return batchIds[0] || 'Batch';
  const [first, ...rest] = batchIds;
  const prefix = (first.match(/^(.*-)/) || [])[1] || '';
  return [first, ...rest.map((id) => (prefix && id.startsWith(prefix) ? id.slice(prefix.length) : id))].join('+');
}

// hideLabels: at the production house labels are not their job (the warehouse prints them when receiving)
// inline: buttons only (inside the production house "Next step" card); primaryFiles: print files is the main button
export default function BatchPaperwork({ api, batchId, can, toast, onChanged, hideLabels = false, inline = false, primaryFiles = false }) {
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null); // 'labels' | 'together' | 'tiff'
  const [plan, setPlan] = useState(null); // print together: merged files + which batch each part belongs to
  const sheetRef = useRef(null);
  const labels = useMemo(() => batchLabelRows(batch), [batch]);
  const showSheet = can('batch.sheet'); const showLabels = can('batch.labels') && !hideLabels; const showFiles = can('batch.files');
  if (!showSheet && !showLabels && !showFiles) return null;
  const load = async () => {
    if (batch) return batch;
    const r = await api(`/api/v9/production-batches/${encodeURIComponent(batchId)}`);
    setBatch(r.batch);
    return r.batch;
  };
  const run = async (what) => {
    setBusy(what);
    try {
      const b = await load();
      if (what === 'sheet') { await new Promise((res) => setTimeout(res, 50)); printNode(sheetRef.current?.firstChild, b.batchId); }
      else setOpen(what === 'tiff' ? 'together' : what); // print files: first choose what to print together
    } catch (e) { toast?.(e.message, true); } finally { setBusy(null); }
  };
  return (
    <div className={`od-actions ${inline ? 'od-paperwork-inline' : 'od-paperwork'}`} aria-label="Production paperwork">
      {!inline && <span className="od-small">Paperwork</span>}
      {showFiles && primaryFiles && <button className="od-btn od-pri" disabled={!!busy} onClick={() => run('tiff')}>{busy === 'tiff' ? 'Loading…' : 'Print files (TIFF)'}</button>}
      {showSheet && <button className="od-btn" disabled={!!busy} onClick={() => run('sheet')}>{busy === 'sheet' ? 'Preparing…' : 'Batch PDF'}</button>}
      {showLabels && <button className="od-btn" disabled={!!busy} onClick={() => run('labels')}>{busy === 'labels' ? 'Loading…' : 'Labels'}</button>}
      {showFiles && !primaryFiles && <button className="od-btn" disabled={!!busy} onClick={() => run('tiff')}>{busy === 'tiff' ? 'Loading…' : 'Print files (TIFF)'}</button>}
      {batch && <div ref={sheetRef} style={{ display: 'none' }}><BatchPrintSheet batch={batch} /></div>}
      {batch && open === 'labels' && <LabelPrintModal isOpen onClose={() => setOpen(null)} modeContext="batch" batchId={batch.batchId || batchId} batchLabelRows={labels} />}
      {batch && open === 'together' && (
        <Modal isOpen onClose={() => setOpen(null)} contentClassName="od-pt-modal">
          <PrintTogether api={api} batchId={batch.batchId || batchId} onCancel={() => setOpen(null)} onReady={(p) => { setPlan(p); setOpen('tiff'); }} />
        </Modal>
      )}
      {batch && open === 'tiff' && plan && (
        <ProductionTiffExportModal isOpen onClose={() => { setOpen(null); setPlan(null); setBatch(null); }} batch={batch} items={plan.items} folderName={runFolderName(plan.batchIds)}
          subtitle={<>{plan.batchIds.length > 1 ? 'Printing together' : 'Batch'}: <strong>{plan.batchIds.join(', ')}</strong> ({plan.quality || batch.batchQuality})</>}
          onRecord={(summary) => api('/api/v1/order-desk/print-runs', 'POST', { baseBatchId: batch.batchId || batchId, parts: plan.parts, summary })}
          onRunSuccess={() => { onChanged?.(); toast?.(plan.batchIds.length > 1 ? `Sent for printing together: ${plan.batchIds.join(', ')}` : 'Sent for printing'); }} />
      )}
    </div>
  );
}
